'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { Zap, Shield, AlertTriangle, Info, Building2, Activity } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const aptCampaigns = [
  { id: 'apt-001', group: 'APT41 (Winnti)', origin: 'China', target: 'Financial Infrastructure / SWIFT', severity: 'Critical', status: 'Active', confidence: 82, ttps: 'Spear-phishing, supply-chain compromise, SWIFT targeting', acmeRelevance: 'APAC financial operations at elevated risk' },
  { id: 'apt-002', group: 'Sandworm', origin: 'Russia', target: 'Energy / Industrial Control Systems', severity: 'High', status: 'Active', confidence: 78, ttps: 'ICS/SCADA exploitation, destructive malware', acmeRelevance: 'Energy procurement systems — monitor for anomalies' },
  { id: 'apt-003', group: 'Lazarus Group', origin: 'North Korea', target: 'Cryptocurrency / Financial Theft', severity: 'High', status: 'Active', confidence: 74, ttps: 'Social engineering, watering hole attacks, crypto theft', acmeRelevance: 'Finance team exposure — phishing risk elevated' },
  { id: 'apt-004', group: 'Cozy Bear (APT29)', origin: 'Russia', target: 'Government / Defence / Tech', severity: 'Medium', status: 'Monitoring', confidence: 69, ttps: 'Credential theft, long-term persistence, cloud exploitation', acmeRelevance: 'Low direct exposure — monitor for sector spillover' },
];

const severityColor: Record<string, string> = {
  Critical: 'text-danger bg-danger/10',
  High: 'text-warning bg-warning/10',
  Medium: 'text-info bg-info/10',
};

const statusColor: Record<string, string> = {
  Active: 'text-danger',
  Monitoring: 'text-warning',
  Resolved: 'text-success',
};

export default function CyberRiskPage() {
  return (
    <AppLayout activeRoute="/cyber-risk">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Cyber Risk Intelligence</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Nation-state APT campaigns, ransomware groups, and critical infrastructure threats</p>
          </div>
          <Link href="/alerts" className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 transition-all">
            View Cyber Alerts
          </Link>
        </div>

        {/* How teams use this */}
        <div className="card-elevated rounded-xl border border-info/20 bg-info/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-info flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-info uppercase tracking-wide mb-1">How teams use this</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Security teams use APT campaign tracking to map active threat actor TTPs against their network exposure profile, prioritise patching and segmentation, and generate board-ready cyber risk summaries with sector-specific escalation probabilities.
              </p>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Cyber Threat Score', value: '68', unit: '/ 100', icon: Activity, color: 'text-warning', sub: 'Elevated — 5 active campaigns' },
            { label: 'Active APT Campaigns', value: '5', unit: 'groups', icon: Zap, color: 'text-danger', sub: '2 targeting financial sector' },
            { label: 'Critical Vulnerabilities', value: '14', unit: 'CVEs', icon: AlertTriangle, color: 'text-warning', sub: 'Unpatched in monitored systems' },
            { label: 'Acme Corp Exposure', value: 'Medium', unit: '', icon: Building2, color: 'text-info', sub: 'APAC financial ops at risk' },
          ].map(({ label, value, unit, icon: Icon, color, sub }) => (
            <div key={label} className="card-elevated rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className={color} />
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <p className={`text-2xl font-bold font-tabular ${color}`}>{value}</p>
                {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
              </div>
              <p className="text-2xs text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* Acme Corp Cyber Exposure */}
        <div className="card-elevated rounded-xl border border-warning/20 bg-warning/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 size={14} className="text-warning" />
            <p className="text-sm font-semibold text-foreground">Acme Corp — Cyber Exposure Assessment</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            APT41 campaign targeting SWIFT messaging infrastructure creates elevated risk for Acme Corp APAC financial operations. 3 monitored IP ranges overlap with published IOCs. Cyber incident increases exposure for APAC financial operations — immediate network segmentation review recommended.
          </p>
        </div>

        {/* APT Campaign Table */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <p className="text-sm font-semibold text-foreground">Active APT Campaigns</p>
            <p className="text-xs text-muted-foreground mt-0.5">Nation-state and advanced persistent threat groups currently active</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Threat Group</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Origin</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Primary Target</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Severity</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Confidence</th>
                  <th className="px-4 py-3 text-left font-semibold text-muted-foreground uppercase tracking-wider">Acme Relevance</th>
                </tr>
              </thead>
              <tbody>
                {aptCampaigns.map((apt, idx) => (
                  <tr key={apt.id} className={`border-b border-border hover:bg-muted/30 transition-colors ${idx % 2 === 1 ? 'bg-muted/10' : ''}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{apt.group}</td>
                    <td className="px-4 py-3 text-muted-foreground">{apt.origin}</td>
                    <td className="px-4 py-3 text-muted-foreground">{apt.target}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-2xs font-semibold ${severityColor[apt.severity]}`}>{apt.severity}</span>
                    </td>
                    <td className={`px-4 py-3 font-medium ${statusColor[apt.status]}`}>{apt.status}</td>
                    <td className="px-4 py-3 text-muted-foreground font-tabular">{apt.confidence}%</td>
                    <td className="px-4 py-3 text-muted-foreground">{apt.acmeRelevance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Risk Score Methodology */}
        <div className="card-elevated rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Cyber Risk Score Methodology</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Cyber threat score (68/100) is an AI-weighted composite combining: active APT campaign severity (40%), unpatched critical CVE exposure (25%), sector-specific targeting frequency (20%), and company network exposure overlap (15%). Score adjusts dynamically based on new IOC publications, CISA advisories, and monitored asset changes.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
